/**
 * O que o portal relata à API de atividade (`POST /v1/audit/events`).
 *
 * Só o que o servidor não vê: navegação e ações que não passam por uma rota
 * (abrir um produto, buscar). Matrícula, curtida, anotação, inscrição e afins
 * o próprio servidor registra. A lista espelha as ações de cliente do contexto
 * `audit` no back — chave fora dela é descartada lá.
 *
 * Privacidade: ids, títulos e contagens. Nunca o que a pessoa digitou (o
 * termo da busca vai como número de caracteres) nem a query string da URL.
 */

export type ClientAction =
  | 'session.login'
  | 'session.logout'
  | 'page.viewed'
  | 'search.performed'
  | 'share.opened'
  | 'onboarding.completed'
  | 'onboarding.skipped'
  | 'course.viewed'
  | 'product.viewed'
  | 'product.favorited'
  | 'product.unfavorited'
  | 'event.viewed'
  | 'assistant.opened';

export type TrackedTarget = 'lesson' | 'course' | 'product' | 'event';

export type Metadata = Record<string, string | number | boolean>;

export interface TrackInput {
  action: ClientAction;
  targetType?: TrackedTarget;
  targetId?: string;
  /** Nome do que foi aberto (título do produto, da aula) ou da página. */
  targetLabel?: string;
  metadata?: Metadata;
  /** Caminho da rota, sem query string. Padrão: a página atual. */
  path?: string;
}

/** Um evento como vai no corpo do lote. */
export interface WireEvent {
  action: ClientAction;
  target_type?: TrackedTarget;
  target_id?: string;
  target_label?: string;
  path: string;
  at: string;
  metadata?: Metadata;
}

/** Teto do lote na API. */
export const MAX_BATCH = 20;
/** De quanto em quanto tempo a fila é enviada. */
export const FLUSH_INTERVAL_MS = 5_000;
/** O mesmo evento (ação, alvo e página) dentro deste intervalo conta uma vez. */
export const DEDUPE_MS = 1_000;
const LABEL_CHARS = 200;

export function toWire(input: TrackInput, path: string, at: number): WireEvent {
  const event: WireEvent = { action: input.action, path: path.slice(0, 300), at: new Date(at).toISOString() };
  if (input.targetId) {
    event.target_id = input.targetId;
    if (input.targetType) event.target_type = input.targetType;
  }
  const label = input.targetLabel?.replace(/\s+/g, ' ').trim();
  if (label) event.target_label = label.slice(0, LABEL_CHARS);
  if (input.metadata && Object.keys(input.metadata).length > 0) event.metadata = input.metadata;
  return event;
}
